import {
  ColumnsEyeExerciseView,
  FocusCornersExerciseView,
  GrowingSquaresExerciseView,
  GuidedHorizontalZigzagExerciseView,
  GuidedVerticalZigzagExerciseView,
  HorizontalZigzagEyeExerciseView,
  HorizontalZigzagTwoEyeExerciseView,
  HourglassEyeExerciseView,
  MetronomeEyeExerciseView,
  PonteCircularEyeExerciseView,
  ThirteenPointEyeExerciseView,
  VerticalZigzagTwoEyeExerciseView,
  VerticalZigzagEyeExerciseView,
} from './EyeExerciseViews';
import { AttentionExerciseView } from './AttentionExerciseViews';
import { CaptureExerciseView, ConcreteMeaningExerciseView, MatchingExerciseView, PeripheryFocusExerciseView, PilotTestExerciseView, SimilarWordsExerciseView, TextSearchExerciseView, WordGlanceExerciseView, WordSearchExerciseView } from './PracticeExerciseViews';
import { HomeworkPlanView, ReadingAssessmentModeView } from './ProgramExerciseViews';
import { ModesGrid, getModeCanonicalKey, type ModeConfig, type ModeSection, type RecommendedStoryContent } from './speedReadingShared';
import { BlockReadingExerciseView, GroupingExerciseView, TextReadingExerciseView } from './StoryExerciseViews';
import { SpeedReadingActivityTracker } from './speedReadingActivity';
import React from 'react';

type SelectedModeRendererProps = {
  profile: any;
  modeSections: ModeSection[];
  modes: ModeConfig[];
  selectedMode: ModeConfig | null;
  selectedModeContent: RecommendedStoryContent | null;
  modeContentLoading: boolean;
  onCloseMode: () => void;
  onSelectMode: (mode: ModeConfig) => void;
};

// Açık uygulama yönetici raporu için kayda alınır; mod listesi (seçim yokken) kaydedilmez.
export const SelectedModeRenderer: React.FC<SelectedModeRendererProps> = (props) => {
  const { selectedMode } = props;
  if (!selectedMode) return <SelectedModeView {...props} />;

  return (
    <SpeedReadingActivityTracker key={selectedMode.name} mode={selectedMode}>
      <SelectedModeView {...props} />
    </SpeedReadingActivityTracker>
  );
};

const SelectedModeView: React.FC<SelectedModeRendererProps> = ({
  profile,
  modeSections,
  modes,
  selectedMode,
  selectedModeContent,
  modeContentLoading,
  onCloseMode,
  onSelectMode,
}) => {
  if (!selectedMode) return <ModesGrid onSelectMode={onSelectMode} sections={modeSections} modes={modes} />;

  const modeKey = getModeCanonicalKey(selectedMode);

  if (['metin-okuma', 'eksiltili-metin', 'serbest-okuma', 'odaklanarak-okuma', 'anlama-calismalari'].includes(modeKey)) {
    return (
      <TextReadingExerciseView
        key={selectedMode.name}
        profile={profile}
        mode={selectedMode}
        recommendedContent={selectedModeContent}
        contentLoading={modeContentLoading}
        onClose={onCloseMode}
      />
    );
  };

  if (modeKey === 'okuma-anlama-seviye-testi') {
    return <ReadingAssessmentModeView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'blok-okuma') {
    return <BlockReadingExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'metronom-goz-sektirme') {
    return <MetronomeEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'buyuyen-sonsuz-kareler') {
    return <GrowingSquaresExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'odaklan-ve-koseleri-gor') {
    return <FocusCornersExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === '13-nokta-goz-egzersizi') {
    return <ThirteenPointEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'ponte-dairesel-goz-egzersizi') {
    return <PonteCircularEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'dikey-zikzak-goz-egzersizi') {
    return <VerticalZigzagEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'dikey-zikzak-2-goz-egzersizi') {
    return <VerticalZigzagTwoEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'yatay-zikzak-goz-egzersizi') {
    return <HorizontalZigzagEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'yatay-zikzak-2-goz-egzersizi') {
    return <HorizontalZigzagTwoEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'kum-saati-egzersizi') {
    return <HourglassEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'goz-egzersizleri-dikey-zikzak') {
    return <GuidedVerticalZigzagExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'goz-egzersizleri-kolonlar') {
    return <ColumnsEyeExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'goz-egzersizleri-yatay-zikzak') {
    return <GuidedHorizontalZigzagExerciseView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'gunluk-odevler') {
    return <HomeworkPlanView key={selectedMode.name} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (modeKey === 'takistoskop') return <PilotTestExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (modeKey === 'bir-bakista-gorelim' || modeKey === 'gozlerimizi-sektirerek-gorelim') return <WordGlanceExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (modeKey === 'kelime-arama') return <WordSearchExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (modeKey === 'benzer-kelimeler') return <SimilarWordsExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (modeKey === 'cevre-odak') return <PeripheryFocusExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (modeKey === 'somut-anlam') return <ConcreteMeaningExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (modeKey === 'metin-arama') {
    return (
      <TextSearchExerciseView
        key={selectedMode.name}
        profile={profile}
        mode={selectedMode}
        recommendedContent={selectedModeContent}
        contentLoading={modeContentLoading}
        onClose={onCloseMode}
      />
    );
  }
  if (modeKey === 'hedef-yakalama') {
    return (
      <CaptureExerciseView
        key={selectedMode.name}
        profile={profile}
        mode={selectedMode}
        recommendedContent={selectedModeContent}
        contentLoading={modeContentLoading}
        onClose={onCloseMode}
      />
    );
  };

  if (modeKey === 'kaybolan-metin') {
    return (
      <CaptureExerciseView
        key={selectedMode.name}
        profile={profile}
        mode={selectedMode}
        recommendedContent={selectedModeContent}
        contentLoading={modeContentLoading}
        variant="horizontal"
        onClose={onCloseMode}
      />
    );
  };

  if (['multi-fokus', 'zit-anlam', 'es-anlam'].includes(modeKey)) return <MatchingExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  if (['dikkat-puzzle', 'rengi-bul', 'harf-sayma', 'sayi-sayma', 'iki-resim-arasindaki-fark'].includes(modeKey)) {
    return <AttentionExerciseView key={selectedMode.name} profile={profile} mode={selectedMode} onClose={onCloseMode} />;
  }

  if (['grouping', 'golgeleme', 'bloklama', 'sihirli-hiz'].includes(modeKey)) {
    return (
      <GroupingExerciseView
        key={selectedMode.name}
        profile={profile}
        mode={selectedMode}
        recommendedContent={selectedModeContent}
        contentLoading={modeContentLoading}
        onClose={onCloseMode}
      />
    );
  };

  return <ModesGrid onSelectMode={onSelectMode} sections={modeSections} modes={modes} />;
};
